import React, { useState } from 'react';
import classNames from 'classnames';
import { Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import { getOrdersTracking } from '../../api/orders';
import { Notifications } from '../../store/slices';
import { useStyles } from './styles';

const OrdersTrackingPage = () => {
    const classes = useStyles();
    const [fileData, setFileData] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [processed, setProcessed] = useState(false);
    const [fileName, setFileName] = useState<string>('');
    const workbookRef = React.useRef<any>(null);
    const dispatch = useDispatch();

    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const processFile = (file: File) => {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const bstr = event.target?.result;
                const wb = XLSX.read(bstr, { type: 'binary', cellNF: true, cellStyles: true });
                workbookRef.current = wb;
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data: any[] = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });

                if (data.length > 0) {
                    const headers = Object.keys(data[0] as object);
                    setHeaders(headers);
                    setFileData(data);
                    setProcessed(false);
                    
                    // Automatic processing
                    await processData(data, headers, wb);
                } else {
                    dispatch(Notifications.setNotifications({ text: 'File is empty', type: 'error' }));
                }
            } catch (err) {
                console.error(err);
                dispatch(Notifications.setNotifications({ text: 'Error parsing file', type: 'error' }));
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        processFile(file);
        e.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && (file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.csv'))) {
            processFile(file);
        } else {
            dispatch(Notifications.setNotifications({ text: 'Please upload a valid Excel or CSV file', type: 'error' }));
        }
    };

    const handlePaperClick = () => {
        fileInputRef.current?.click();
    };

    const processData = async (data: any[], currentHeaders: string[], wb: XLSX.WorkBook) => {
        let orderIdKey = currentHeaders.find(h => h.toLowerCase().trim() === 'order id' || h === 'orderId' || h === 'OrderID');
        const trackingNumKey = currentHeaders.find(h => h.toLowerCase().trim() === 'tracking #' || h === 'Tracking #');
        const printedMessageKey = currentHeaders.find(h => h.toLowerCase().trim() === 'printed message');
        
        if (!orderIdKey && !printedMessageKey) {
            dispatch(Notifications.setNotifications({ text: 'No "Order ID" or "Printed Message" column found in the file', type: 'error' }));
            return;
        }

        if (!orderIdKey) {
            orderIdKey = 'Order ID';
            currentHeaders.push(orderIdKey);
        }

        const orderIdArr = data
            .map(row => {
                let id = row[orderIdKey as string];
                if (!id && printedMessageKey && row[printedMessageKey]) {
                    const match = String(row[printedMessageKey]).match(/^\s*(\d+)/);
                    if (match) {
                        id = match[1];
                        row[orderIdKey as string] = id;
                    }
                }
                if (!id) return null;
                const cleanedId = String(id).trim();
                return cleanedId ? (/^\d+$/.test(cleanedId) ? Number(cleanedId) : cleanedId) : null;
            })
            .filter(id => id !== null);

        if (orderIdArr.length === 0) {
            dispatch(Notifications.setNotifications({ text: 'No valid order IDs found', type: 'error' }));
            return;
        }

        setLoading(true);
        try {
            const response = await getOrdersTracking(orderIdArr);
            let trackingResult: any[] = [];
            if (Array.isArray(response.result)) {
                trackingResult = response.result;
            } else if (response.result && Array.isArray(response.result.result)) {
                trackingResult = response.result.result;
            } else if (Array.isArray(response)) {
                trackingResult = response;
            }

            const trackingMap = new Map();
            trackingResult.forEach((item: any) => {
                trackingMap.set(String(item.orderId).trim(), item);
            });

            // Filter and update UI data
            const matchedRows: any[] = [];
            data.forEach(row => {
                const originalId = row[orderIdKey as string];
                const cleanedId = originalId ? String(originalId).trim() : '';
                const trackingInfo = trackingMap.get(cleanedId);
                
                const fileTrackingNum = trackingNumKey ? String(row[trackingNumKey]).trim() : '';
                const apiTrackingId = trackingInfo ? String(trackingInfo.trackingId).trim() : '';

                // Match logic: must have API info AND Tracking # must match
                if (trackingInfo && (!trackingNumKey || fileTrackingNum === apiTrackingId)) {
                    matchedRows.push({
                        ...row,
                        'Client ID': trackingInfo?.clientId !== undefined && trackingInfo?.clientId !== null ? trackingInfo.clientId : row['Client ID'] || '',
                        'Client Name': trackingInfo?.clientName !== undefined && trackingInfo?.clientName !== null ? trackingInfo.clientName : row['Client Name'] || '',
                        'Tracking ID': trackingInfo?.trackingId !== undefined && trackingInfo?.trackingId !== null ? trackingInfo.trackingId : row['Tracking ID'] || '',
                    });
                }
            });

            if (matchedRows.length === 0) {
                dispatch(Notifications.setNotifications({ text: 'No matches found between file and API results', type: 'warning' }));
                setFileData([]);
                setProcessed(true);
                return;
            }

            const newHeaders = new Set(currentHeaders);
            newHeaders.add('Client ID');
            newHeaders.add('Client Name');
            newHeaders.add('Tracking ID');

            const headersList = Array.from(newHeaders);
            setHeaders(headersList);
            setFileData(matchedRows);

            // Create a new worksheet with only matched rows, preserving original cell formats
            if (wb) {
                const wsname = wb.SheetNames[0];
                const originalWs = wb.Sheets[wsname];
                const range = XLSX.utils.decode_range(originalWs['!ref'] || "A1:A1");
                
                // Find column indices
                let orderIdColIdx = -1;
                let trackingNumColIdx = -1;
                let printedMessageColIdx = -1;
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = originalWs[XLSX.utils.encode_cell({ c: C, r: range.s.r })];
                    if (cell && cell.v) {
                        const val = String(cell.v).toLowerCase().trim();
                        if (val === 'order id' || val === 'orderid') orderIdColIdx = C;
                        if (val === 'tracking #') trackingNumColIdx = C;
                        if (val === 'printed message') printedMessageColIdx = C;
                    }
                }

                const newWs: XLSX.WorkSheet = { '!ref': '' };
                let newRowIdx = 0;

                // Copy header row
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = originalWs[XLSX.utils.encode_cell({ c: C, r: range.s.r })];
                    if (cell) newWs[XLSX.utils.encode_cell({ c: C, r: newRowIdx })] = { ...cell };
                }

                // Add new column headers
                const getOrCreateColIdx = (name: string, currentMaxCol: number) => {
                    for (let C = 0; C <= currentMaxCol; ++C) {
                        const cell = newWs[XLSX.utils.encode_cell({ c: C, r: 0 })];
                        if (cell && cell.v && String(cell.v).trim() === name) return C;
                    }
                    return -1;
                };

                let lastColIdx = range.e.c;
                const getOrAddHeader = (name: string) => {
                    let idx = getOrCreateColIdx(name, lastColIdx);
                    if (idx === -1) {
                        idx = ++lastColIdx;
                        newWs[XLSX.utils.encode_cell({ c: idx, r: 0 })] = { v: name, t: 's' };
                    }
                    return idx;
                };

                const orderIdNewCol = orderIdColIdx !== -1 ? orderIdColIdx : getOrAddHeader('Order ID');
                const clientIdCol = getOrAddHeader('Client ID');
                const clientNameCol = getOrAddHeader('Client Name');
                const trackingIdCol = getOrAddHeader('Tracking ID');

                newRowIdx++;

                // Copy matched data rows
                for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                    let extractedId = '';
                    if (orderIdColIdx !== -1) {
                        const idCell = originalWs[XLSX.utils.encode_cell({ c: orderIdColIdx, r: R })];
                        if (idCell && idCell.v) extractedId = String(idCell.w || idCell.v).trim();
                    }
                    
                    if (!extractedId && printedMessageColIdx !== -1) {
                        const pmCell = originalWs[XLSX.utils.encode_cell({ c: printedMessageColIdx, r: R })];
                        if (pmCell && pmCell.v) {
                            const pmMatch = String(pmCell.w || pmCell.v).match(/^\s*(\d+)/);
                            if (pmMatch) extractedId = pmMatch[1];
                        }
                    }

                    if (!extractedId) continue;
                    
                    const cleanedId = extractedId;
                    const trackingInfo = trackingMap.get(cleanedId);
                    
                    const trackingCell = trackingNumColIdx !== -1 ? originalWs[XLSX.utils.encode_cell({ c: trackingNumColIdx, r: R })] : null;
                    const fileTrackingNum = trackingCell ? String(trackingCell.w || trackingCell.v).trim() : '';
                    const apiTrackingId = trackingInfo ? String(trackingInfo.trackingId).trim() : '';

                    if (trackingInfo && (trackingNumColIdx === -1 || fileTrackingNum === apiTrackingId)) {
                        // Copy original cells for this row
                        for (let C = range.s.c; C <= range.e.c; ++C) {
                            const cell = originalWs[XLSX.utils.encode_cell({ c: C, r: R })];
                            if (cell) newWs[XLSX.utils.encode_cell({ c: C, r: newRowIdx })] = { ...cell };
                        }
                        
                        // Add tracking info
                        newWs[XLSX.utils.encode_cell({ c: orderIdNewCol, r: newRowIdx })] = { v: extractedId, t: /^\d+$/.test(extractedId) ? 'n' : 's' };
                        if (trackingInfo.clientId) newWs[XLSX.utils.encode_cell({ c: clientIdCol, r: newRowIdx })] = { v: trackingInfo.clientId, t: 'n' };
                        if (trackingInfo.clientName) newWs[XLSX.utils.encode_cell({ c: clientNameCol, r: newRowIdx })] = { v: trackingInfo.clientName, t: 's' };
                        if (trackingInfo.trackingId) newWs[XLSX.utils.encode_cell({ c: trackingIdCol, r: newRowIdx })] = { v: trackingInfo.trackingId, t: 's' };
                        
                        newRowIdx++;
                    }
                }

                newWs['!ref'] = XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: lastColIdx, r: newRowIdx - 1 } });
                wb.Sheets[wsname] = newWs;
            }

            setProcessed(true);
            dispatch(Notifications.setNotifications({ text: `Tracking data retrieved successfully. Found ${matchedRows.length} matches.`, type: 'success' }));
        } catch (error: any) {
            console.error(error);
            dispatch(Notifications.setNotifications({ text: error.message || 'Failed to fetch tracking data', type: 'error' }));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        const isCsv = fileName.toLowerCase().endsWith('.csv');
        const outName = isCsv ? "orders_tracking_filtered.csv" : "orders_tracking_filtered.xlsx";
        if (workbookRef.current) {
            XLSX.writeFile(workbookRef.current, outName);
        } else {
            const ws = XLSX.utils.json_to_sheet(fileData, { header: headers });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Orders Tracking");
            XLSX.writeFile(wb, outName);
        }
    };

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="API Integration"
                title="Orders tracking"
                subtitle="Upload a carrier spreadsheet; the rows whose order and tracking number match get the client and tracking id filled in."
                actions={(
                    <>
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            accept=".xls,.xlsx,.csv"
                            onChange={handleFileUpload}
                            disabled={loading}
                        />
                        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={handleDownload} disabled={!processed || fileData.length === 0}>
                            Download filtered
                        </Button>
                        <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} onClick={handlePaperClick} disabled={loading}>
                            Upload file
                        </Button>
                    </>
                )}
            />

            {loading ? (
                <Panel>
                    <div className={classes.processing} role="status">
                        <CircularProgress size={28} />
                        <span>Processing {fileName || 'the file'}…</span>
                    </div>
                </Panel>
            ) : fileData.length > 0 ? (
                <Panel
                    title={fileName || 'Matched rows'}
                    badges={<StatusChip size="sm" tone="success">{fileData.length} matched</StatusChip>}
                    padding="none"
                    footer={fileData.length > 100 ? 'Showing the first 100 matched rows. The downloaded file contains all of them.' : undefined}
                >
                    <div className={classes.scroll}>
                        <Table stickyHeader aria-label="Matched rows">
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, index) => (
                                        <TableCell key={index}>{header}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fileData.slice(0, 100).map((row, rowIndex) => (
                                    <TableRow key={rowIndex} hover>
                                        {headers.map((header, colIndex) => (
                                            <TableCell key={colIndex} className={classes.cell} title={String(row[header] ?? '')}>{row[header]}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Panel>
            ) : processed ? (
                <Panel>
                    <EmptyState
                        title="No matches found"
                        hint="None of the rows matched an order with the same tracking number."
                        action={<Button variant="outlined" size="small" onClick={handlePaperClick}>Upload another file</Button>}
                    />
                </Panel>
            ) : (
                <div
                    className={classNames(classes.dropZone, { [classes.dragging]: isDragging })}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handlePaperClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handlePaperClick();
                        }
                    }}
                    aria-label="Upload a spreadsheet"
                >
                    <span className={classes.dropIcon} aria-hidden="true"><UploadFileOutlinedIcon /></span>
                    <p className={classes.dropTitle}>Drag and drop a file here</p>
                    <p className={classes.dropHint}>Excel or CSV, or click to choose one</p>
                </div>
            )}
        </PageContainer>
    );
};

export default OrdersTrackingPage;
